import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { adminAPI } from '../../services/apiService';
import { Bell, Send, ArrowLeft } from 'lucide-react';

const SendNotification = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    link: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const isAdminAuth = localStorage.getItem('isAdminAuthenticated');
    if (!isAdminAuth) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error('Title and message are required');
      return;
    }

    setIsLoading(true);

    try {
      const notificationData = {
        title: formData.title,
        message: formData.message,
        link: formData.link || null
      };

      const response = await adminAPI.createNotification(notificationData);
      
      toast.success(response.message || 'Notification sent successfully!');
      
      // Reset form
      setFormData({
        title: '',
        message: '',
        link: ''
      });
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Send Notification
            </h1>
            <p className="text-gray-700 text-lg">Broadcast message to all active users</p>
          </div>
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="outline"
            className="border-2 border-gray-300"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        {/* Form Card */}
        <Card className="border-4 border-red-400 shadow-2xl bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}>
                <Bell className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">Create Broadcast Notification</CardTitle>
                <CardDescription>This will be sent to all active users</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title Field */}
              <div className="space-y-2">
                <Label htmlFor="title" className="text-gray-700 font-semibold text-lg">
                  Notification Title *
                </Label>
                <Input
                  id="title"
                  name="title"
                  placeholder="e.g., New Commission Structure Update"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="h-14 text-lg border-2"
                />
              </div>

              {/* Message Field */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-gray-700 font-semibold text-lg">
                  Notification Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Enter your message here..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="text-lg border-2 resize-none"
                />
                <p className="text-sm text-gray-600">
                  {formData.message.length} characters
                </p>
              </div>

              {/* Link Field */}
              <div className="space-y-2">
                <Label htmlFor="link" className="text-gray-700 font-semibold text-lg">
                  Link (Optional)
                </Label>
                <Input
                  id="link"
                  name="link"
                  type="url"
                  placeholder="https://example.com/more-info"
                  value={formData.link}
                  onChange={handleChange}
                  className="h-14 text-lg border-2"
                />
                <p className="text-sm text-gray-600">
                  Add a link for users to learn more (optional)
                </p>
              </div>

              {/* Preview */}
              {(formData.title || formData.message) && (
                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold text-lg">Preview</Label>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Bell className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-bold text-gray-900">{formData.title || 'Title'}</p>
                        <p className="text-sm text-gray-700 mt-1">{formData.message || 'Message'}</p>
                        {formData.link && (
                          <a href={formData.link} target="_blank" rel="noopener noreferrer" 
                             className="text-sm text-blue-600 hover:underline mt-2 inline-block">
                            View More →
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 h-14 text-lg font-bold text-white border-2 border-yellow-400 shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
                >
                  {isLoading ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Send to All Active Users
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className="mt-6 p-6 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-2">📢 Important Notes:</h3>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
            <li>This notification will be sent to <strong>all active users</strong></li>
            <li>Users will see it in their notification bell icon</li>
            <li>Notifications appear immediately in real-time</li>
            <li>Users can mark notifications as read/unread</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SendNotification;
